import { Injectable } from '@angular/core';
import {
	ApiService,
	AppSettingsService,
	GlobalErrorHandlerService,
	ListType,
	ShopperService,
} from '@woolworthsnz/styleguide';
import {
	AddOrUpdateTrolleyRequestV2,
	AddOrUpdateTrolleyResponseV2,
	PastOrderItemsSearchRequest,
	PastOrderListItem,
	PastOrderPagedResponse,
	SavedListResponse,
	SaveNewListRequest,
	SaveNewListResponse,
	SearchResponse,
	ShopperFavouritesSearchRequest,
} from '@woolworthsnz/trader-api';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import AddToBasketSourceEnum = AddOrUpdateTrolleyRequestV2.AddToBasketSourceEnum;
import AddFromListSourceEnum = SaveNewListRequest.AddFromListSourceEnum;

@Injectable({
	providedIn: 'root',
})
export class ListService {
	constructor(
		private shopperService: ShopperService,
		private globalErrorHandlerService: GlobalErrorHandlerService,
		private appSettingsService: AppSettingsService,
		private apiService: ApiService
	) {}

	fetchMyFavourites(params: ShopperFavouritesSearchRequest): Observable<SearchResponse> {
		return this.shopperService.getShopperFavourites(params).pipe(
			catchError((e) => {
				this.globalErrorHandlerService.handleError(e);
				return throwError(e);
			})
		);
	}

	fetchPastOrders(): Observable<PastOrderPagedResponse> {
		return this.shopperService.getShopperPastOrders();
	}

	fetchPastOrder(orderId: number): Observable<PastOrderListItem> {
		return this.shopperService.getShopperPastOrder(orderId);
	}

	fetchPastOrderItems(
		params: PastOrderItemsSearchRequest,
		orderId: number | 'all' = 'all'
	): Observable<SearchResponse> {
		return this.shopperService.getShopperPastOrderItems(params, orderId).pipe(
			catchError((e) => {
				this.globalErrorHandlerService.handleError(e);
				return throwError(e);
			})
		);
	}

	fetchSavedListItems(params: PastOrderItemsSearchRequest, listItemId?: number): Observable<SearchResponse> {
		return this.apiService.get(`${this.appSettingsService.getEndpoint('savedList')}/${listItemId}/items`, {
			params,
		});
	}

	addPastOrderItemsToTrolley(orderId: number | 'all'): Observable<AddOrUpdateTrolleyResponseV2> {
		return this.shopperService.addPastOrderItemsToTrolley(orderId);
	}

	addAllToTrolley(listItemId?: number | string, source?: ListType): Observable<any> {
		const body: AddOrUpdateTrolleyRequestV2 = {};

		// try to parse it else make it undefined
		if (typeof listItemId === 'string') {
			const listItemIdAsInt = parseInt(listItemId, 10);
			if (!isNaN(listItemIdAsInt)) {
				listItemId = listItemIdAsInt;
			} else {
				listItemId = undefined;
			}
		}

		switch (source) {
			case ListType.PastOrders:
				body.addToBasketSource = listItemId
					? AddToBasketSourceEnum.PastOrdersAllItems
					: AddToBasketSourceEnum.PastOrdersMasterAllItems;
				body.orderId = listItemId;
				break;
			case ListType.Favourites:
				body.addToBasketSource = AddToBasketSourceEnum.FavouritesAllItems;
				break;
			case ListType.SavedList:
				body.addToBasketSource = AddToBasketSourceEnum.SavedListAllItems;
				body.savedListId = listItemId;
				break;
		}

		return this.shopperService.addAllToTrolley(body);
	}

	addSavedListFromCurrentTrolley(listName: string): Observable<SaveNewListResponse> {
		return this.addSavedListFromExistingList(listName, AddFromListSourceEnum.Trolley);
	}

	addSavedListFromFavourites(listName: string): Observable<SaveNewListResponse> {
		return this.addSavedListFromExistingList(listName, AddFromListSourceEnum.FavouritesAllItems);
	}

	addSavedListFromExistingSavedList(listName: string, existingListId: number): Observable<SaveNewListResponse> {
		return this.addSavedListFromExistingList(listName, AddFromListSourceEnum.MySavedList, existingListId);
	}

	addSavedListFromExistingPastOrder(
		listName: string,
		existingListId: number | 'all'
	): Observable<SaveNewListResponse> {
		if (existingListId === 'all') {
			return this.addSavedListFromExistingList(listName, AddFromListSourceEnum.PastOrdersMasterAllItems);
		}
		return this.addSavedListFromExistingList(listName, AddFromListSourceEnum.PastOrders, existingListId);
	}

	addSavedListFromExistingList(
		listName: string,
		listSource: AddFromListSourceEnum,
		existingListId?: number
	): Observable<SaveNewListResponse> {
		const body: SaveNewListRequest = {
			listName,
			addFromListSource: listSource,
		};
		if (existingListId) {
			body.sourceListId = existingListId;
		}
		return this.shopperService.addSavedList(body);
	}

	fetchSavedLists(): Observable<SavedListResponse> {
		return this.apiService.get(this.appSettingsService.getEndpoint('savedList'));
	}

	deleteSavedList(listId: number): Observable<void> {
		return this.apiService
			.delete(`${this.appSettingsService.getEndpoint('savedList')}/${listId}`)
			.pipe(map(() => undefined));
	}
}
